import { Button, CircularProgress, Link, Paper, useMediaQuery, useTheme } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import schemas from '../../../data/schemas';
import {
  createCollaborator,
  resetCollaboratorTwoFactor,
  unlockCollaborator,
  updateCollaborator,
} from '../../../store/action_creators/collaborators.actions';
import { Status, UserTypeEnum } from '../../../store/config/enums';
import {
  Collaborator,
  CreateCollaboratorRequest,
  RootState,
  UpdateCollaboratorRequest,
} from '../../../store/config/types';
import { CustomSnackbar } from '../../CustomSnackbar';
import PasswordField from '../../PasswordField/PasswordField';
import { ReturnButton } from '../../ReturnButton';
import { ScreenTitle } from '../../ScreenTitle';
import { SubmitButton } from '../../SubmitButton';
import { ChangePasswordDialog } from '../../dialogs/ChangePasswordDialog';
import { DeleteDialog } from '../../dialogs/DeleteDialog';
import { FormTextField } from '../FormTextField';

interface UserState {
  collaborator: Collaborator | null;
}

interface Values {
  names: string;
  surnames: string;
  email: string;
  password: string;
  repeatPassword: string;
}

function SubUser() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const userState = location.state as UserState;
  const client = location.state?.client;
  const collaborator = userState?.collaborator;
  const { auth, collaborators } = useSelector((state: RootState) => state);
  const [creatingCollaborator, setCreatingCollaborator] = useState<boolean>(false);
  const [updatingCollaborator, setUpdatingCollaborator] = useState<boolean>(false);
  const [resetingPassword, setResetingPassword] = useState<boolean>(false);
  const [resetingTwoFactor, setResetingTwoFactor] = useState<boolean>(false);
  const [unlockingUser, setUnlockingUser] = useState<boolean>(false);
  const [passwordOpen, setPasswordOpen] = useState<boolean>(false);
  const [resetTwoFactorOpen, setResetTwoFactorOpen] = useState<boolean>(false);
  const [unlockUserOpen, setUnlockUserOpen] = useState<boolean>(false);

  const isSuperAdmin = auth.account?.type === UserTypeEnum.SUPERADMIN;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const submitCollaborator = (values: Values, helpers: FormikHelpers<Values>) => {
    if (auth.account) {
      if (collaborator) {
        setUpdatingCollaborator(true);

        const updateCollaboratorRequest: UpdateCollaboratorRequest = {
          userId: collaborator.userId,
          email: collaborator.email,
          businessId: auth.account?.business?.id || -1,
          names: values.names,
          surnames: values.surnames,
          telephoneNo: collaborator.telephoneNo,
          type: 2,
        };

        dispatch(updateCollaborator(updateCollaboratorRequest));
      } else {
        setCreatingCollaborator(true);

        const createCollaboratorRequest: CreateCollaboratorRequest = {
          active: true,
          businessId: auth.account?.business?.id || -1,
          names: values.names,
          surnames: values.surnames,
          email: values.email,
          password: values.password,
          telephoneNo: '1',
        };

        dispatch(createCollaborator(createCollaboratorRequest));
      }
    }
  };

  const closeSnack = () => {
    if (collaborator) {
      setUpdatingCollaborator(false);
      setResetingPassword(false);
    } else {
      setCreatingCollaborator(false);
    }
    isSuperAdmin ? navigate('/manage-clients', { state: { client: client } }) : navigate(-1);
  };

  const closeTwoFactorSnack = () => {
    setResetingTwoFactor(false);
  };

  const closeUnlockSnack = () => {
    setUnlockingUser(false);
  };

  const handleResetTwoFactor = () => {
    if (collaborator) {
      setResetTwoFactorOpen(false);
      setResetingTwoFactor(true);
      dispatch(resetCollaboratorTwoFactor(collaborator.userId));
    }
  };

  const handleUnlockUser = () => {
    if (collaborator) {
      setUnlockUserOpen(false);
      setUnlockingUser(true);
      dispatch(unlockCollaborator(collaborator.userId));
    }
  };

  const securityActionButton = (label: string, loading: boolean, onClick: () => void) => (
    <Button type="button" variant="contained" color="primary" onClick={onClick} disabled={loading}>
      {loading ? <CircularProgress size={20} thickness={6} color="inherit" /> : label}
    </Button>
  );

  const submitButton = (
    <SubmitButton
      formId="collaboratorForm"
      loading={creatingCollaborator || updatingCollaborator}
      label={
        collaborator ? `Guardar ${!isMobile ? 'cambios' : ''}` : `Crear ${!isMobile ? 'colaborador' : ''}`
      }
    />
  );

  return (
    <div className="screen-container profile">
      <ScreenTitle
        title={collaborator ? 'Editar colaborador' : 'Nuevo colaborador'}
        hasReturnButton={!isMobile && !isSuperAdmin}
      >
        {isSuperAdmin && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/manage-clients', { state: { client: client } })}
          >
            Volver
          </Button>
        )}
        {!isMobile && submitButton}
      </ScreenTitle>
      <Paper elevation={2}>
        <Formik
          initialValues={{
            names: collaborator ? collaborator.names : '',
            surnames: collaborator ? collaborator.surnames : '',
            email: collaborator ? collaborator.email : '',
            password: collaborator ? 'Password1234!' : '',
            repeatPassword: collaborator ? 'Password1234!' : '',
          }}
          validationSchema={schemas.UserSchema}
          onSubmit={submitCollaborator}
        >
          <Form className="form" id="collaboratorForm">
            <Field
              className="form-row-first"
              name="names"
              component={FormTextField}
              type="text"
              placeholder="Nombres"
            />
            <Field
              className="form-row"
              name="surnames"
              component={FormTextField}
              type="text"
              placeholder="Apellidos"
            />
            <Field
              className="form-row"
              name="email"
              component={FormTextField}
              type="text"
              placeholder="Correo electrónico"
              disabled={!!collaborator}
            />
            {collaborator ? (
              <div className="link">
                <Link onClick={() => setPasswordOpen(true)}>
                  <u>Cambiar contraseña</u>
                </Link>
              </div>
            ) : (
              <>
                <PasswordField inputName="password" />
                <PasswordField inputName="repeatPassword" placeholder="Repetir contraseña" />
              </>
            )}
            {collaborator && isSuperAdmin && (
              <div className="security-actions">
                <div className="security-action-row">
                  <span>Reinicio de verificación 2FA</span>
                  {securityActionButton(
                    'Reiniciar',
                    collaborators.resettingCollaboratorTwoFactor,
                    () => setResetTwoFactorOpen(true),
                  )}
                </div>
                <div className="security-action-row">
                  <span>Desbloquear usuario por intentos incorrectos</span>
                  {securityActionButton(
                    'Desbloquear',
                    collaborators.unlockingCollaborator,
                    () => setUnlockUserOpen(true),
                  )}
                </div>
              </div>
            )}
            <div className="many-actions-row">
              {isMobile && (
                <>
                  <ReturnButton />
                  {submitButton}
                </>
              )}
            </div>
            <CustomSnackbar
              open={
                creatingCollaborator &&
                (collaborators.createCollaboratorSuccess ||
                  collaborators.createCollaboratorErrorMessage !== null)
              }
              message={
                collaborators.createCollaboratorSuccess
                  ? 'Se creó el colaborador correctamente.'
                  : collaborators.createCollaboratorErrorMessage!
              }
              handleClose={closeSnack}
              type={collaborators.createCollaboratorSuccess ? Status.SUCCESS : Status.ERROR}
            />
            <CustomSnackbar
              open={
                updatingCollaborator &&
                (collaborators.updateCollaboratorSuccess ||
                  collaborators.updateCollaboratorErrorMessage !== null)
              }
              message={
                collaborators.updateCollaboratorSuccess
                  ? 'Se actualizó el colaborador correctamente.'
                  : collaborators.updateCollaboratorErrorMessage!
              }
              handleClose={closeSnack}
              type={collaborators.updateCollaboratorSuccess ? Status.SUCCESS : Status.ERROR}
            />
            <CustomSnackbar
              open={
                resetingPassword && (auth.resetPasswordSuccess || auth.resetPasswordErrorMessage !== null)
              }
              message={
                auth.resetPasswordSuccess
                  ? 'Se reinició la contraseña del colaborador correctamente.'
                  : 'Ocurrió un error al reiniciar la contraseña del colaborador.'
              }
              handleClose={closeSnack}
              type={auth.resetPasswordSuccess ? Status.SUCCESS : Status.ERROR}
            />
            <CustomSnackbar
              open={
                resetingTwoFactor &&
                (collaborators.resetCollaboratorTwoFactorSuccess ||
                  collaborators.resetCollaboratorTwoFactorErrorMessage !== null)
              }
              message={
                collaborators.resetCollaboratorTwoFactorSuccess
                  ? 'Se reinició la verificación 2FA correctamente.'
                  : collaborators.resetCollaboratorTwoFactorErrorMessage!
              }
              handleClose={closeTwoFactorSnack}
              type={collaborators.resetCollaboratorTwoFactorSuccess ? Status.SUCCESS : Status.ERROR}
            />
            <CustomSnackbar
              open={
                unlockingUser &&
                (collaborators.unlockCollaboratorSuccess ||
                  collaborators.unlockCollaboratorErrorMessage !== null)
              }
              message={
                collaborators.unlockCollaboratorSuccess
                  ? 'Se desbloqueó el usuario correctamente.'
                  : collaborators.unlockCollaboratorErrorMessage!
              }
              handleClose={closeUnlockSnack}
              type={collaborators.unlockCollaboratorSuccess ? Status.SUCCESS : Status.ERROR}
            />
            <ChangePasswordDialog open={passwordOpen} setOpen={setPasswordOpen} collaborator={collaborator} />
            <DeleteDialog
              open={resetTwoFactorOpen}
              setOpen={setResetTwoFactorOpen}
              message="¿Confirmas que quieres reiniciar la verificación 2FA de este usuario?"
              deleteAction={handleResetTwoFactor}
              deleteButtonText="Reiniciar"
              deleteDisabled={collaborators.resettingCollaboratorTwoFactor}
            />
            <DeleteDialog
              open={unlockUserOpen}
              setOpen={setUnlockUserOpen}
              message="¿Confirmas que quieres desbloquear este usuario por intentos incorrectos?"
              deleteAction={handleUnlockUser}
              deleteButtonText="Desbloquear"
              deleteDisabled={collaborators.unlockingCollaborator}
            />
          </Form>
        </Formik>
      </Paper>
    </div>
  );
}

export default SubUser;
